import { createSchema } from '@better-fetch/fetch'
import { z } from 'zod'

export const SUPPORTED_LOCALES = ['en', 'fr', 'de', 'ja', 'pl', 'cn'] as const
export const SUPPORTED_COUNTRIES = ['US', 'GB', 'FR', 'DE', 'NL', 'AT', 'CA', 'IL', 'AU', 'CN', 'PL', 'CH', 'NZ', 'BE', 'IE'] as const

// ? Fallback to string to avoid breaking the system :(
export const CountryCodeSchema = z.enum(SUPPORTED_COUNTRIES).or(z.string())
export type CountryCode = z.infer<typeof CountryCodeSchema>

export const LangCodeSchema = z.enum(SUPPORTED_LOCALES)
export type LangCode = z.infer<typeof LangCodeSchema>

const ItemSchema = z.object({
  hubId: z.string(),
  rNumber: z.string().optional(),
  imageURL: z.string(),
  title: z.string(),
  area: z.string().optional(),
  state: z.enum(['available', 'selected', 'disabled']).nullable(),
  amount: z.number(),
  position: z.enum(['horizontal', 'vertical']).nullable().optional(),
  height: z.enum(['fill', 'fit']).nullable().optional(),
  label: z.string(),
})

export type Item = z.infer<typeof ItemSchema>

export const SendConfigurationOutputSchema = z.object({
  isSingleProductConfiguration: z.literal(1).or(z.literal(0)),
  email: z.string().email(),
  cartItems: z.array(z.any()),
  hubConfig: z.record(z.string(), z.number()).optional(),
  configImages: z.array(ItemSchema),
  lang: LangCodeSchema,
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  country: z.string().optional(),
  subscribeNewsletter: z.boolean(),
  hubPrices: z.array(z.string()),
})
export type SendEmailBody = z.infer<typeof SendConfigurationOutputSchema>

export const SendConfigurationParamsSchema = z.object({
  isocode: CountryCodeSchema,
  lang: LangCodeSchema,
})
export type SendConfigurationParams = z.infer<typeof SendConfigurationParamsSchema>

export const ProductDataSchema = z.object({
  id: z.string().nullish(),
  RNumber: z.string().nullish(),
  Model: z.string().nullish(),
  ShortForm: z.string().nullish(),
  Color: z.string().nullish(),
  MyCtrl_IncludedInText: z.null(),
  product_description: z.string().nullish(),
  thumbnailImage: z.string().nullish(),
}).partial()
export type ProductData = z.infer<typeof ProductDataSchema>

export const ProductDetailsOutput = z.object({
  infoMessage: z.string(),
  failure: z.boolean(),
  productData: ProductDataSchema,
  lang: z.string(),
})
export type ProductDetailsOutputType = z.infer<typeof ProductDetailsOutput>

export const CurrencySchema = z.enum([
  'USD', // United States
  'GBP', // United Kingdom
  'EUR', // Europe
  'CAD', // Canada
  'ILS', // Israel
  'CNY', // China
  'PLN', // Poland
])
export type Currency = z.infer<typeof CurrencySchema>

export const PricingSchema = z.object({
  retailprice: z.coerce.string(),
  currency: CurrencySchema.or(z.string()),
  country: CountryCodeSchema,
  priceperunit: z.coerce.string().optional().nullable(),
  0: z.number().optional(),
})
export type Pricing = z.infer<typeof PricingSchema>

export const PartSchema = z.object({
  id: z.string(),
  partname: z.string(),
  RNumber: z.string(),
  OriginalRNumber: z.string().optional(),
  RNumberSpecial: z.string().optional(),
  sortOrder: z.string(),
  pricing: PricingSchema,
  part_limit: z.coerce.number(),
  productDetailsthumbnailImage: z.string(),
})
export type Part = z.infer<typeof PartSchema>

export const CategorySchema = z.object({
  id: z.string(),
  categoryname: z.string(),
  // categoryname_pl: z.any(),
  sortOrder: z.string(),
  categoryType: z.string(),
  parts: z.array(PartSchema),
})
export type Category = z.infer<typeof CategorySchema>

export const HubSchema = z.object({
  id: z.string(),
  name: z.string(),
  RNumber: z.string(),
  sortOrder: z.string(),
  amountLimitSensors: z.string(),
  amountLimitControls: z.string(),
  amountLimit12VOirt: z.string(),
  amountLimitPSUs: z.string(),
  pricing: PricingSchema,
  categories: z.array(CategorySchema),
})
export type Hub = z.infer<typeof HubSchema>

export const InitDataSchema = z.object({
  hubs: z.array(HubSchema),
})
export type InitData = z.infer<typeof InitDataSchema>

export const InitBodySchema = z.object({
  lang: LangCodeSchema,
  isocode: CountryCodeSchema,
})
export type InitBody = z.infer<typeof InitBodySchema>

export const CountrySchema = z.object({
  id: z.string(),
  countryname: z.string(),
  isocode2: CountryCodeSchema,
  has_state_selection: z.string(),
  preselection_by_ip: z.boolean(),
})
export type Country = z.infer<typeof CountrySchema>

export const CountryListBody = z.object({
  lang: LangCodeSchema,
})

export const CountryListSchema = z.object({
  infoMessage: z.string(),
  lang: z.string(),
  data: z.array(CountrySchema),
})
export type CountryList = z.infer<typeof CountryListSchema>

export const apiSchema = createSchema({
  'mycontroller/send-configuration/json': {
    method: 'post',
    input: SendConfigurationOutputSchema,
    output: z.any(),
  },
  'mycontroller/init/json': {
    method: 'post',
    input: InitBodySchema,
    output: InitDataSchema,
  },
  'products/my-controller-details/:rnumber/:language/json': {
    method: 'get',
    output: ProductDetailsOutput,
  },
  'list-countries/mycontroller/json': {
    method: 'post',
    input: CountryListBody,
    output: CountryListSchema,
  },
})
